import React, { createContext, useState, useCallback, useEffect, useContext, useRef } from 'react';
import { notification as antdNotification } from 'antd';
import { DataContext } from './DataProvider';
import {
  fetchNotificationsPaged,
  pollTranscriptions,
  markAllNotificationsSeen,
  markAllNotificationsRead,
  markNotificationRead,
  clearAllNotifications as clearAllMeetingNotifications,
} from '../services/meetings.service';
import { fetchTeamsTranscriptsAvailable } from '../services/microsoft.service';
import {
  WikiNotification,
  fetchWikiNotifications,
  fetchWikiUnreadCount,
  markWikiNotificationRead,
  markAllWikiNotificationsRead,
  deleteWikiNotification,
  clearAllWikiNotifications,
} from '../services/wiki.service';

export interface Notification {
  id: string;
  meetingId?: string;
  type?: 'meeting' | 'agent';
  sessionId?: string;
  transcriptionId?: string;
  status: 'loading' | 'success' | 'error';
  title: string;
  message: string;
  createdAt?: string;
  isRead?: boolean;
  isSeen?: boolean;
}

interface PollingItem {
  recordingId: string;
  meetingId: string;
  transcriptionId: string;
}

export interface TeamsImportItem {
  meetingId: string;
  orgId: string;
  teamsOnlineMeetingId: string;
  recordingId: string;
  transcriptId: string | null;
  startedAt: number;
}

const TEAMS_IMPORT_LS_KEY = 'prospo_teams_import_queue';
const TEAMS_IMPORT_TTL_MS = 10 * 60 * 1000; // 10 minutes
const TEAMS_IMPORT_POLL_INTERVAL_MS = 8_000;
const TEAMS_IMPORT_MAX_ERRORS = 5;

function readQueueFromStorage(): TeamsImportItem[] {
  try {
    const raw = localStorage.getItem(TEAMS_IMPORT_LS_KEY);
    if (!raw) return [];
    const items: TeamsImportItem[] = JSON.parse(raw);
    const now = Date.now();
    return items.filter(i => now - i.startedAt < TEAMS_IMPORT_TTL_MS);
  } catch {
    return [];
  }
}

function writeQueueToStorage(items: TeamsImportItem[]) {
  try {
    localStorage.setItem(TEAMS_IMPORT_LS_KEY, JSON.stringify(items));
  } catch {
    // ignore storage errors
  }
}

interface NotificationContextType {
  notifications: Notification[];
  badgeCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  addNotification: (notification: Notification) => void;
  startPollingForTranscript: (item: PollingItem) => void;
  markAllAsSeen: () => void;
  markAllAsRead: () => void;
  markOneAsRead: (notificationId: string) => void;
  clearAllNotifications: () => void;
  completedTranscriptData: { recordingId: string | null; transcript: any | null; timestamp: number | null };
  clearCompletedTranscript: () => void;
  loadMore: () => void;
  // Teams import
  importingTeamsRecordings: TeamsImportItem[];
  completedTeamsImports: Array<{ meetingId: string; recordingId: string }>;
  startTeamsImportPolling: (item: TeamsImportItem) => void;
  clearCompletedTeamsImport: (meetingId: string) => void;
  // Wiki notifications
  wikiNotifications: WikiNotification[];
  wikiUnreadCount: number;
  wikiHasMore: boolean;
  wikiLoadingMore: boolean;
  loadMoreWikiNotifications: () => void;
  markWikiRead: (id: string) => void;
  markAllWikiRead: () => void;
  deleteWikiNotif: (id: string) => void;
  clearAllWikiNotifs: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  badgeCount: 0,
  hasMore: false,
  loadingMore: false,
  addNotification: () => { },
  startPollingForTranscript: () => { },
  markAllAsSeen: () => { },
  markAllAsRead: () => { },
  markOneAsRead: () => { },
  clearAllNotifications: () => { },
  completedTranscriptData: { recordingId: null, transcript: null, timestamp: null },
  clearCompletedTranscript: () => { },
  loadMore: () => { },
  importingTeamsRecordings: [],
  completedTeamsImports: [],
  startTeamsImportPolling: () => { },
  clearCompletedTeamsImport: () => { },
  wikiNotifications: [],
  wikiUnreadCount: 0,
  wikiHasMore: false,
  wikiLoadingMore: false,
  loadMoreWikiNotifications: () => { },
  markWikiRead: () => { },
  markAllWikiRead: () => { },
  deleteWikiNotif: () => { },
  clearAllWikiNotifs: () => { },
});

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [pollingItems, setPollingItems] = useState<PollingItem[]>([]);
  const [completedTranscriptData, setCompletedTranscriptData] = useState<{ recordingId: string | null; transcript: any | null; timestamp: number | null }>({ recordingId: null, transcript: null, timestamp: null });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const isPollingRef = useRef(false);
  const { login } = useContext(DataContext);

  useEffect(() => {
    const handleAgentNotification = (event: Event) => {
      const notification = (event as CustomEvent<Notification>).detail;
      if (!notification?.id?.startsWith('agent-')) return;
      setNotifications(prev => {
        if (prev.some(item => item.id === notification.id)) return prev;
        return [notification, ...prev];
      });
    };
    window.addEventListener('prospo:add-agent-notification', handleAgentNotification);
    return () => window.removeEventListener('prospo:add-agent-notification', handleAgentNotification);
  }, []);

  // ── Teams import state ──────────────────────────────────────────────────────
  const [importingTeamsRecordings, setImportingTeamsRecordings] = useState<TeamsImportItem[]>(
    () => readQueueFromStorage()
  );
  const [completedTeamsImports, setCompletedTeamsImports] = useState<Array<{ meetingId: string; recordingId: string }>>([]);
  const isTeamsPollingRef = useRef(false);
  // Consecutive error count per recordingId — reset on success, remove item on >= TEAMS_IMPORT_MAX_ERRORS
  const teamsImportErrorCountsRef = useRef<Record<string, number>>({});
  // Tracks which synthetic notification IDs we've already added to the bell panel this session
  const addedTeamsNotifIdsRef = useRef<Set<string>>(new Set());

  // Sync importingTeamsRecordings → localStorage on every change
  useEffect(() => {
    writeQueueToStorage(importingTeamsRecordings);
  }, [importingTeamsRecordings]);

  // Add a loading entry to the bell panel for each in-queue item.
  // The ref guard ensures we add it only once per session even if the effect re-runs
  // (e.g. when another item is removed from the queue later).
  // This also handles page-reload re-hydration from localStorage.
  useEffect(() => {
    importingTeamsRecordings.forEach(item => {
      const notifId = `teams-import-${item.recordingId}`;
      if (!addedTeamsNotifIdsRef.current.has(notifId)) {
        addedTeamsNotifIdsRef.current.add(notifId);
        setNotifications(prev => {
          if (prev.some(n => n.id === notifId)) return prev;
          return [{
            id: notifId,
            meetingId: item.meetingId,
            status: 'loading' as const,
            title: 'Importing from Teams',
            message: 'Recording is being processed, this may take a few minutes.',
            createdAt: new Date(item.startedAt).toISOString(),
            isRead: false,
            isSeen: false,
          }, ...prev];
        });
      }
    });
  }, [importingTeamsRecordings]);

  const badgeCount = notifications.filter(n => !n.isSeen).length;

  useEffect(() => {
    const fetchUserNotifications = async () => {
      if (login?.data?.id) {
        try {
          const res: any = await fetchNotificationsPaged(1, 10);
          if (res.success) {
            const fetchedNotifications = res.notifications.map((n: any) => ({
              id: n.recordingId,
              meetingId: n.meetingId,
              transcriptionId: n.transcriptionId,
              status: n.status,
              title: n.title,
              message: n.message,
              isRead: n.isRead,
              isSeen: n.isSeen,
              createdAt: n.createdAt,
            }));
            // Merge: preserve frontend-only notifications that have no backend row.
            // (ids prefixed 'teams-import-') that aren't yet in the backend list.
            // Without this, the backend fetch would wipe the loading indicator
            // that was added before the async fetch completed.
            setNotifications(prev => {
              const frontendOnlyNotifs = prev.filter(
                n => (n.id.startsWith('teams-import-') || n.id.startsWith('agent-'))
                  && !fetchedNotifications.some((f: Notification) => f.id === n.id)
              );
              return [...frontendOnlyNotifs, ...fetchedNotifications];
            });
            setHasMore(res.hasMore);
            setPage(1);

            const pendingItems = res.notifications
              .filter((n: any) => n.status === 'loading' && n.transcriptionId)
              .map((n: any) => ({
                recordingId: n.recordingId,
                meetingId: n.meetingId,
                transcriptionId: n.transcriptionId,
              }));
            setPollingItems(pendingItems);
          }
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      }
    };
    fetchUserNotifications();
  }, [login?.data?.id]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res: any = await fetchNotificationsPaged(nextPage, 10);
      if (res.success) {
        const newNotifications = res.notifications.map((n: any) => ({
          id: n.recordingId,
          meetingId: n.meetingId,
          transcriptionId: n.transcriptionId,
          status: n.status,
          title: n.title,
          message: n.message,
          isRead: n.isRead,
          isSeen: n.isSeen,
          createdAt: n.createdAt,
        }));
        setNotifications(prev => [...prev, ...newNotifications]);
        setHasMore(res.hasMore);
        setPage(nextPage);
      }
    } catch (error) {
      console.error("Failed to load more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  useEffect(() => {
    if (pollingItems.length === 0 || !login?.data?.id) {
      return;
    }

    const intervalId = setInterval(async () => {
      // Skip this tick if the previous request is still in-flight.
      // Without this guard, setInterval fires every 5s regardless of response time —
      // a slow name-mapping call (~10s) would cause 2+ concurrent requests that all
      // return success simultaneously, firing multiple toasts for the same transcription.
      if (isPollingRef.current) return;
      isPollingRef.current = true;
      try {
        const transcriptionIds = pollingItems.map(item => item.transcriptionId);
        const res: any = await pollTranscriptions(transcriptionIds);

        if (!res.success) return;

        const completedTranscriptsMap = new Map(res.transcriptions.map((t: any) => [t._id.toString(), t]));
        const failedIds = new Set<string>(res.failedTranscriptionIds || []);

        if (completedTranscriptsMap.size > 0 || failedIds.size > 0) {
          setNotifications(prev => prev.map(n => {
            const pollingItem = pollingItems.find(p => p.recordingId === n.id);
            if (!pollingItem) return n;

            if (completedTranscriptsMap.has(pollingItem.transcriptionId)) {
              const notificationMessage =
                res.notificationMessages?.[pollingItem.transcriptionId] ||
                'Your audio file has been successfully transcribed.';
              if (n.status === 'loading') {
                antdNotification.success({
                  message: 'Transcription Ready!',
                  description: notificationMessage,
                  duration: 4,
                  placement: 'topRight',
                  style: { cursor: 'pointer' },
                  onClick: () => { window.location.href = `/meetings/${n.meetingId}`; },
                });
              }
              const transcriptData = completedTranscriptsMap.get(pollingItem.transcriptionId);
              setCompletedTranscriptData({
                recordingId: pollingItem.recordingId,
                transcript: transcriptData,
                timestamp: Date.now(),
              });
              return { ...n, status: 'success', title: 'Transcription Ready!', message: notificationMessage };
            }

            if (failedIds.has(pollingItem.transcriptionId)) {
              setCompletedTranscriptData({
                recordingId: pollingItem.recordingId,
                transcript: null,
                timestamp: Date.now(),
              });
              return { ...n, status: 'error', title: 'Transcription Failed', message: 'Transcription failed. You can retry.' };
            }

            return n;
          }));

          const completedIds = new Set(res.transcriptions.map((t: any) => t._id.toString()));
          setPollingItems(prev =>
            prev.filter(item => !completedIds.has(item.transcriptionId) && !failedIds.has(item.transcriptionId))
          );
        }
      } catch (error) {
        console.error("Transcription polling failed:", error);
      } finally {
        isPollingRef.current = false;
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [pollingItems, login?.data?.id]);

  // ── Teams import polling ────────────────────────────────────────────────────

  const updateBellNotification = useCallback((notif: Notification) => {
    setNotifications(prev => {
      const idx = prev.findIndex(n => n.id === notif.id);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = notif;
        return updated;
      }
      return [notif, ...prev];
    });
  }, []);

  const runTeamsImportCheck = useCallback(async (items: TeamsImportItem[]) => {
    if (isTeamsPollingRef.current || items.length === 0) return;
    isTeamsPollingRef.current = true;
    try {
      const now = Date.now();
      const staleIds = new Set<string>();

      for (const item of items) {
        if (now - item.startedAt > TEAMS_IMPORT_TTL_MS) {
          staleIds.add(item.recordingId);
          updateBellNotification({
            id: `teams-import-${item.recordingId}`,
            meetingId: item.meetingId,
            status: 'error',
            title: 'Import timed out',
            message: 'The recording could not be imported. Please try again.',
            createdAt: new Date(item.startedAt).toISOString(),
            isRead: false,
            isSeen: false,
          });
        }
      }

      if (staleIds.size > 0) {
        setImportingTeamsRecordings(prev => prev.filter(i => !staleIds.has(i.recordingId)));
      }

      const activeItems = items.filter(i => !staleIds.has(i.recordingId));
      const errorFailedIds = new Set<string>();

      for (const item of activeItems) {
        try {
          const res: any = await fetchTeamsTranscriptsAvailable(item.orgId, item.teamsOnlineMeetingId, item.meetingId);

          if (!res.success) {
            const count = (teamsImportErrorCountsRef.current[item.recordingId] ?? 0) + 1;
            teamsImportErrorCountsRef.current[item.recordingId] = count;
            if (count >= TEAMS_IMPORT_MAX_ERRORS) {
              errorFailedIds.add(item.recordingId);
            }
            continue;
          }

          const recording = res.recordings?.find((r: any) => r.id === item.recordingId);
          if (recording?.imported) {
            delete teamsImportErrorCountsRef.current[item.recordingId];
            setImportingTeamsRecordings(prev => prev.filter(i => i.recordingId !== item.recordingId));
            setCompletedTeamsImports(prev => [...prev, { meetingId: item.meetingId, recordingId: item.recordingId }]);
            // Update the bell notification to success — isSeen: false bumps the badge count
            updateBellNotification({
              id: `teams-import-${item.recordingId}`,
              meetingId: item.meetingId,
              status: 'success',
              title: 'Teams recording ready',
              message: 'Your Teams recording and transcript have been imported successfully.',
              createdAt: new Date(item.startedAt).toISOString(),
              isRead: false,
              isSeen: false,
            });
            // Replace the loading toast with a success toast (same key swaps it in-place)
            antdNotification.success({
              key: `teams-import-toast-${item.recordingId}`,
              message: 'Teams recording ready',
              description: 'Your Teams recording and transcript have been imported successfully.',
              duration: 5,
              placement: 'topRight',
              style: { cursor: 'pointer' },
              onClick: () => { window.location.href = `/meetings/${item.meetingId}`; },
            });
          } else {
            // Successful response but recording not yet imported — reset error counter
            teamsImportErrorCountsRef.current[item.recordingId] = 0;
          }
        } catch {
          const count = (teamsImportErrorCountsRef.current[item.recordingId] ?? 0) + 1;
          teamsImportErrorCountsRef.current[item.recordingId] = count;
          if (count >= TEAMS_IMPORT_MAX_ERRORS) {
            errorFailedIds.add(item.recordingId);
          }
        }
      }

      if (errorFailedIds.size > 0) {
        errorFailedIds.forEach(recordingId => {
          delete teamsImportErrorCountsRef.current[recordingId];
          const item = items.find(i => i.recordingId === recordingId);
          updateBellNotification({
            id: `teams-import-${recordingId}`,
            meetingId: item?.meetingId ?? '',
            status: 'error',
            title: 'Import failed',
            message: 'The recording could not be imported after multiple attempts. Please try again.',
            createdAt: item ? new Date(item.startedAt).toISOString() : new Date().toISOString(),
            isRead: false,
            isSeen: false,
          });
        });
        setImportingTeamsRecordings(prev => prev.filter(i => !errorFailedIds.has(i.recordingId)));
      }
    } finally {
      isTeamsPollingRef.current = false;
    }
  }, [updateBellNotification]);

  useEffect(() => {
    if (importingTeamsRecordings.length === 0 || !login?.data?.id) return;

    // Fire immediately so returning to a finished import clears the ghost fast
    runTeamsImportCheck(importingTeamsRecordings);

    const intervalId = setInterval(() => {
      runTeamsImportCheck(importingTeamsRecordings);
    }, TEAMS_IMPORT_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [importingTeamsRecordings, runTeamsImportCheck, login?.data?.id]);

  // ── Public API ──────────────────────────────────────────────────────────────

  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => {
      const existingIndex = prev.findIndex(n => n.id === notification.id);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = notification;
        return updated;
      }
      return [notification, ...prev];
    });
  }, []);

  const startPollingForTranscript = useCallback((item: PollingItem) => {
    setPollingItems(prev => [...prev, item]);
  }, []);

  const startTeamsImportPolling = useCallback((item: TeamsImportItem) => {
    setImportingTeamsRecordings(prev => {
      // Deduplicate: skip if same recordingId already in queue
      if (prev.some(i => i.recordingId === item.recordingId)) return prev;
      return [...prev, item];
    });
    // Bell panel notification is added by the importingTeamsRecordings useEffect above.
    // Also show a persistent toast so the user sees it without opening the bell.
    // Using a key so the success toast can replace it in-place when done.
    antdNotification.info({
      key: `teams-import-toast-${item.recordingId}`,
      message: 'Importing from Teams',
      description: 'Recording is being processed, this may take a few minutes.',
      duration: 0,
      placement: 'topRight',
    });
  }, []);

  const clearCompletedTeamsImport = useCallback((meetingId: string) => {
    setCompletedTeamsImports(prev => prev.filter(i => i.meetingId !== meetingId));
  }, []);

  const markAllAsSeen = useCallback(async () => {
    const hasUnseen = notifications.some(n => !n.isSeen);
    if (!hasUnseen) return;

    setNotifications(prev => prev.map(n => ({ ...n, isSeen: true })));

    try {
      await markAllNotificationsSeen();
    } catch (error) {
      console.error(error);
    }
  }, [notifications]);

  const markAllAsRead = useCallback(async () => {
    const hasUnread = notifications.some(n => !n.isRead);
    if (!hasUnread) return;

    try {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, isSeen: true })));
      await markAllNotificationsRead();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, [notifications]);

  const markOneAsRead = useCallback(async (notificationId: string) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || (notification.isRead && notification.isSeen)) {
      return;
    }

    try {
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, isRead: true, isSeen: true } : n))
      );
      if (notificationId.startsWith('agent-') || notificationId.startsWith('teams-import-')) return;
      await markNotificationRead(notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, [notifications]);

  const clearAllNotifications = useCallback(async () => {
    if (notifications.length === 0) return;
    try {
      setNotifications([]);
      await clearAllMeetingNotifications();
    } catch (error) {
      console.error("Failed to clear notifications:", error);
    }
  }, [notifications.length]);

  const clearCompletedTranscript = useCallback(() => {
    setCompletedTranscriptData({ recordingId: null, transcript: null, timestamp: null });
  }, []);

  // ── Wiki notifications ──────────────────────────────────────────────────────

  const [wikiNotifications, setWikiNotifications] = useState<WikiNotification[]>([]);
  const [wikiUnreadCount, setWikiUnreadCount] = useState(0);
  const [wikiPage, setWikiPage] = useState(1);
  const [wikiHasMore, setWikiHasMore] = useState(false);
  const [wikiLoadingMore, setWikiLoadingMore] = useState(false);
  // null = not yet initialised (skip toast on first poll to avoid noise on page load)
  const prevWikiCountRef = useRef<number | null>(null);

  // Initial load + 60s full-list refresh
  useEffect(() => {
    if (!login?.data?.id) return;
    const load = async () => {
      try {
        // Fetch list and accurate total unread count in parallel
        const [result, { count }] = await Promise.all([
          fetchWikiNotifications(1),
          fetchWikiUnreadCount(),
        ]);
        setWikiNotifications(result.notifications);
        setWikiUnreadCount(count);
        setWikiHasMore(result.hasMore);
        setWikiPage(1);
      } catch { /* non-fatal */ }
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [login?.data?.id]);

  // 30s unread-count poll — shows a toast when new notifications arrive
  useEffect(() => {
    if (!login?.data?.id) return;
    prevWikiCountRef.current = null; // reset baseline on login change
    const poll = async () => {
      try {
        const { count } = await fetchWikiUnreadCount();
        if (prevWikiCountRef.current !== null && count > prevWikiCountRef.current) {
          antdNotification.info({
            message: 'New wiki notification',
            description: 'You have new wiki activity. Open the notifications panel to see details.',
            duration: 5,
            placement: 'topRight',
          });
        }
        prevWikiCountRef.current = count;
        setWikiUnreadCount(count);
      } catch { /* non-fatal */ }
    };
    const interval = setInterval(poll, 30_000);
    return () => clearInterval(interval);
  }, [login?.data?.id]);

  const loadMoreWikiNotifications = useCallback(async () => {
    if (wikiLoadingMore || !wikiHasMore) return;
    setWikiLoadingMore(true);
    try {
      const next = wikiPage + 1;
      const result = await fetchWikiNotifications(next);
      setWikiNotifications(prev => [...prev, ...result.notifications]);
      setWikiHasMore(result.hasMore);
      setWikiPage(next);
    } catch { /* non-fatal */ } finally {
      setWikiLoadingMore(false);
    }
  }, [wikiLoadingMore, wikiHasMore, wikiPage]);

  const markWikiRead = useCallback(async (id: string) => {
    setWikiNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    setWikiUnreadCount(prev => Math.max(0, prev - 1));
    try { await markWikiNotificationRead(id); } catch { /* non-fatal */ }
  }, []);

  const markAllWikiRead = useCallback(async () => {
    setWikiNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setWikiUnreadCount(0);
    try { await markAllWikiNotificationsRead(); } catch { /* non-fatal */ }
  }, []);

  const deleteWikiNotif = useCallback(async (id: string) => {
    setWikiNotifications(prev => {
      const target = prev.find(n => n.id === id);
      if (target && !target.isRead) setWikiUnreadCount(c => Math.max(0, c - 1));
      return prev.filter(n => n.id !== id);
    });
    try { await deleteWikiNotification(id); } catch { /* non-fatal */ }
  }, []);

  const clearAllWikiNotifs = useCallback(async () => {
    setWikiNotifications([]);
    setWikiUnreadCount(0);
    try { await clearAllWikiNotifications(); } catch { /* non-fatal */ }
  }, []);

  const contextValue = {
    notifications,
    badgeCount,
    hasMore,
    loadingMore,
    addNotification,
    startPollingForTranscript,
    markAllAsSeen,
    markAllAsRead,
    markOneAsRead,
    clearAllNotifications,
    completedTranscriptData,
    clearCompletedTranscript,
    loadMore,
    importingTeamsRecordings,
    completedTeamsImports,
    startTeamsImportPolling,
    clearCompletedTeamsImport,
    wikiNotifications,
    wikiUnreadCount,
    wikiHasMore,
    wikiLoadingMore,
    loadMoreWikiNotifications,
    markWikiRead,
    markAllWikiRead,
    deleteWikiNotif,
    clearAllWikiNotifs,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
